import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { buildChildrenIndex, type LocationNode } from './types';

/**
 * 可复用位置选择器（架构书 §5 全局交互模式）：树 + 搜索双模式，显示完整路径，
 * 不可存储位置显示琥珀提示但仍可选，已归档位置不出现在候选里。
 *
 * `excludeSubtreeOf`：改父对话框用它排除"自身及其后代"——纯 UI 层面的引导，
 * 真正的防环校验永远在后端（服务层 + DB 触发器），这里只是不让用户白选一个
 * 注定会被拒绝的选项。
 */
export function LocationTreeSelect({
  warehouseId,
  value,
  onChange,
  excludeSubtreeOf,
  allowRoot = true,
}: {
  readonly warehouseId: string;
  readonly value: string | null;
  readonly onChange: (id: string | null) => void;
  readonly excludeSubtreeOf?: string;
  readonly allowRoot?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['locations', warehouseId],
    queryFn: () => apiFetch<{ items: LocationNode[] }>('/locations'),
  });

  const activeNodes = useMemo(
    () => (data?.items ?? []).filter((n) => n.status === 'ACTIVE'),
    [data],
  );
  const excludedPath = excludeSubtreeOf
    ? activeNodes.find((n) => n.id === excludeSubtreeOf)?.path
    : undefined;
  const selectable = useMemo(
    () =>
      excludedPath
        ? activeNodes.filter(
            (n) => n.path !== excludedPath && !n.path.startsWith(`${excludedPath}.`),
          )
        : activeNodes,
    [activeNodes, excludedPath],
  );

  const byId = useMemo(() => new Map(selectable.map((n) => [n.id, n])), [selectable]);
  const childrenIndex = useMemo(() => buildChildrenIndex(selectable), [selectable]);

  const matches = search.trim()
    ? selectable.filter((n) => n.name.toLowerCase().includes(search.trim().toLowerCase()))
    : null;

  const current = value ? byId.get(value) : undefined;

  const pick = (id: string | null): void => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  function renderTree(parentId: string | null, depth: number): React.JSX.Element[] {
    const children = childrenIndex.get(parentId) ?? [];
    return children.flatMap((node) => [
      <button
        key={node.id}
        type="button"
        className="tree__row"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => pick(node.id)}
      >
        <span
          className={
            node.canStoreInventory ? 'tree__label' : 'tree__label tree__label--non-storage'
          }
        >
          {node.name}
        </span>
        {!node.canStoreInventory && (
          <span className="tree__badge" title={t('locations.notStorageHint')}>
            {t('locations.notStorageBadge')}
          </span>
        )}
      </button>,
      ...renderTree(node.id, depth + 1),
    ]);
  }

  return (
    <div className="field" style={{ position: 'relative' }}>
      <button type="button" className="btn btn--ghost" onClick={() => setOpen((o) => !o)}>
        {current
          ? current.path.split('.').length + ' · ' + current.name
          : t('locations.pickLocation')}
      </button>

      {open && (
        <>
          <div className="shell__warehouse-scrim" onClick={() => setOpen(false)} />
          <div
            className="shell__warehouse-menu"
            style={{ width: '320px', maxHeight: '360px', overflowY: 'auto' }}
          >
            <input
              autoFocus
              placeholder={t('app.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 'var(--space-2)' }}
            />
            {allowRoot && !matches && (
              <button
                type="button"
                className="shell__warehouse-menu-item"
                onClick={() => pick(null)}
              >
                {t('locations.warehouseRoot')}
              </button>
            )}
            {matches ? (
              <div className="tree">
                {matches.map((n) => (
                  <button key={n.id} type="button" className="tree__row" onClick={() => pick(n.id)}>
                    <span className="tree__label">{n.name}</span>
                  </button>
                ))}
                {matches.length === 0 && <p className="field-hint">{t('empty.noResults')}</p>}
              </div>
            ) : (
              <div className="tree">{renderTree(null, 0)}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
